import { useState } from "nuxt/app";
import { computed } from "vue";
import { useCategories } from "../storefront/useCategories.js";
import { useFilters } from "../storefront/useFilters.js";
import { useProducts } from "../storefront/useProducts.js";
import { useProductsCount } from "../storefront/useProductsCount.js";
import { extendPromise } from "../../utils/promise.js";
export function useFacet({
  key: optionsKey,
  params = { initialPath: "/" }
} = {}, _key) {
  const key = optionsKey ?? _key ?? "useFacet";
  const currentPath = useState(
    `${key}-path`,
    () => params.initialPath ?? "/"
  );
  const currentPage = useState(
    `${key}-currentPage`,
    () => 1
  );
  const currentPerPage = useState(`${key}-itemsPerPage`, () => 20);
  const currentWhereCondition = useState(
    `${key}-whereCondition`
  );
  const currentSorting = useState(`${key}-sorting`);
  const currentPricePromotionKey = useState(`${key}-pricePromotionKey`);
  const currentCacheParams = useState(
    `${key}-currentCacheParams`
  );
  const currentOrFiltersOperator = useState(
    `${key}-orFilterOperator`
  );
  const productCountWhere = useState(
    `${key}-productCountWhereCondition`
  );
  const categoriesPromise = useCategories({
    params: () => ({
      path: currentPath.value,
      includeHidden: params.with?.product?.categories?.includeHidden || void 0
    }),
    options: { immediate: false },
    key: `${key}-categories`
  });
  const productsPromise = useProducts({
    params: () => ({
      page: currentPage.value,
      perPage: currentPerPage.value,
      with: params.with?.product,
      category: currentPath.value,
      includeSoldOut: params.includeSoldOut,
      includeSellableForFree: params.includeSellableForFree,
      where: currentWhereCondition.value,
      sort: currentSorting.value,
      pricePromotionKey: currentPricePromotionKey.value,
      cache: currentCacheParams.value,
      orFiltersOperator: currentOrFiltersOperator.value
    }),
    options: { immediate: false },
    key: `${key}-products`
  });
  const productsCountPromise = useProductsCount({
    params: () => ({
      category: currentPath.value,
      includedFilters: params.includedFilters,
      includeSoldOut: params.includeSoldOut,
      includeSellableForFree: params.includeSellableForFree,
      where: productCountWhere.value,
      orFiltersOperator: currentOrFiltersOperator.value
    }),
    options: { immediate: false },
    key: `${key}-productCount`
  });
  const filtersPromise = useFilters({
    params: () => ({
      category: currentPath.value,
      includedFilters: params.includedFilters,
      includeSoldOut: params.includeSoldOut,
      includeSellableForFree: params.includeSellableForFree,
      where: currentWhereCondition.value,
      orFiltersOperator: currentOrFiltersOperator.value
    }),
    options: { immediate: false },
    key: `${key}-filters`
  });
  const {
    data: categoryData,
    fetch: refreshCategories,
    fetching: categoriesFetching,
    error: categoriesError,
    status: categoriesStatus
  } = categoriesPromise;
  const {
    data: productData,
    fetch: _refreshProducts,
    fetching: productsFetching,
    error: productError,
    status: productStatus
  } = productsPromise;
  const {
    data: productCountData,
    fetch: _refreshProductCount,
    fetching: productCountFetching,
    error: productCountError,
    status: productCountStatus
  } = productsCountPromise;
  const {
    data: filterData,
    fetch: refreshFilters,
    fetching: filtersFetching,
    error: filterError,
    status: filterStatus
  } = filtersPromise;
  const categories = computed(() => categoryData.value?.categories);
  const selectedCategory = computed(() => categoryData.value?.activeNode);
  const pagination = computed(() => productData.value?.pagination);
  const products = computed(() => productData.value?.products);
  const filters = computed(() => filterData.value?.filters);
  const unfilteredCount = computed(() => filterData.value?.unfilteredCount);
  const refreshProductCount = async ({
    where = void 0
  } = {}) => {
    productCountWhere.value = where;
    if (productCountFetching.value) {
      return;
    }
    await _refreshProductCount();
  };
  const fetchProducts = async ({
    path,
    page = 1,
    perPage = 20,
    where = void 0,
    sort = void 0,
    pricePromotionKey = "",
    cache,
    orFiltersOperator = void 0
  }) => {
    currentPath.value = path;
    currentPage.value = page;
    currentPerPage.value = perPage;
    currentWhereCondition.value = where;
    currentSorting.value = sort;
    currentPricePromotionKey.value = pricePromotionKey;
    currentCacheParams.value = cache;
    currentOrFiltersOperator.value = orFiltersOperator;
    if (categoriesFetching.value || productsFetching.value || filtersFetching.value) {
      return;
    }
    await Promise.all([
      refreshCategories(),
      _refreshProducts(),
      refreshFilters()
    ]);
    return true;
  };
  const filterProducts = async ({
    where = void 0,
    page,
    sort = void 0,
    orFiltersOperator = void 0
  }) => {
    currentWhereCondition.value = where;
    currentPage.value = page;
    currentSorting.value = sort;
    currentOrFiltersOperator.value = orFiltersOperator;
    await _refreshProducts();
  };
  const fetchPage = async (pageToFetch) => {
    currentPage.value = pageToFetch;
    await _refreshProducts();
  };
  return extendPromise(
    Promise.all([
      categoriesPromise,
      productsPromise,
      productsCountPromise,
      filtersPromise
    ]).then(() => ({})),
    {
      // filter data
      filters,
      filtersFetching,
      unfilteredCount,
      filterStatus,
      filterError,
      // filter preview
      productCountData,
      refreshProductCount,
      productCountFetching,
      productCountError,
      productCountStatus,
      // product data
      products,
      pagination,
      productsFetching,
      filterProducts,
      productError,
      productStatus,
      // category data
      categories,
      selectedCategory,
      categoriesFetching,
      categoriesError,
      categoriesStatus,
      // other
      fetchProducts,
      fetchPage
    }
  );
}
